import { useCallback, useMemo } from "react";

import {
  getErrorMessages,
  useGetTransactionsTotalsQuery,
} from "../../../services";
import { TransactionType } from "../../../utils";
import { TransactionsHeroTotals } from "../components/TransactionsHero/types";
import { FilterValues } from "../components/TransactionsFilters/types";
import { PeriodState } from "../TransactionsPage/types";
import { EMPTY_TOTALS } from "../TransactionsPage/utils";
import { GetTransactionsTotalsProps } from "../types";

interface UseTransactionsTotalsProps {
  period: PeriodState;
  filters: FilterValues;
  debouncedSearch: string;
  isMonthPeriod: boolean;
  cashbackBuysOf?: number;
}

export interface UseTransactionsTotalsReturn {
  totals: TransactionsHeroTotals;
  error: string | null;
  refetchTotals: () => Promise<void>;
}

export const useTransactionsTotals = ({
  period,
  filters,
  debouncedSearch,
  isMonthPeriod,
  cashbackBuysOf,
}: UseTransactionsTotalsProps): UseTransactionsTotalsReturn => {
  const totalsParams = useMemo<GetTransactionsTotalsProps>(() => {
    const isAllTime = period.dateRange === "all";
    const categoryId = filters.category
      ? Number(filters.category.value)
      : undefined;
    const accountId = filters.account
      ? Number(filters.account.value)
      : undefined;

    return {
      type: [filters.type.value as TransactionType],
      searchBy: debouncedSearch,
      dateRange: period.dateRange,
      year: isAllTime ? undefined : period.year,
      month: isMonthPeriod ? period.month : undefined,
      categoryId,
      accountId,
      cashbackBuysOf,
    };
  }, [
    period,
    isMonthPeriod,
    filters.type,
    filters.category,
    filters.account,
    cashbackBuysOf,
    debouncedSearch,
  ]);

  const {
    data,
    error: queryError,
    refetch,
  } = useGetTransactionsTotalsQuery(totalsParams);

  const totals = useMemo<TransactionsHeroTotals>(() => {
    if (!data) {
      return EMPTY_TOTALS;
    }
    return {
      income: data.totalIncome,
      expense: data.totalExpense,
      net: data.totalNet,
      count: data.transactionsCount,
    };
  }, [data]);

  const refetchTotals = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const error = queryError
    ? (getErrorMessages(queryError as any)[0] ?? "Failed to load the summary.")
    : null;

  return { totals, error, refetchTotals };
};
